import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { ApiModel, BrandFromGetAll as Brand } from 'shared/models';
import {
    ResponseRetailerGroupImportFeedback,
    RetailerGroup,
    RetailerGroupImport
} from 'app/types/retailer-groups-types';
import { restEndpoint } from 'src/app/config/backend';
import { MarketingFolder } from 'app/content/types/marketing-files';
@Injectable()
export class RetailerGroupsService {
    constructor(private apiModel: ApiModel) {}
    private _selectedGroup = new BehaviorSubject<RetailerGroup>(null);
    public selectedGroup = this._selectedGroup.asObservable();

    private _updateRetailerGroups = new BehaviorSubject<void>(null);
    public updateRetailerGroups = this._updateRetailerGroups.asObservable();

    public getRetailerGroups(brand: Partial<Brand>): Observable<RetailerGroup[]> {
        return this.apiModel
            .makeHttpObservableRequest<RetailerGroup[]>({
                method: 'GET',
                url: `${this.apiModel.getBrandsEndpoint()}/${brand._id}/retailer-groups`
            })
            .pipe(
                map((retailerGroups) =>
                    retailerGroups.map((retailerGroup) => ({ ...retailerGroup, brand: _.pick(brand, ['_id', 'name']) }))
                )
            );
    }

    public getRetailerGroupsByBrandId(brandId: string): Observable<RetailerGroup[]> {
        return this.apiModel
            .makeHttpObservableRequest<RetailerGroup[]>({
                method: 'GET',
                url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/retailer-groups`
            })
            .pipe(map((retailerGroups) => retailerGroups.map((retailerGroup) => ({ ...retailerGroup })))); // TODO: remove map
    }

    public getRetailerGroupsByOrganizationId(organizationId: string, brandIds?: string[], name?: string) {
        return this.apiModel.makeHttpObservableRequest<RetailerGroup[]>({
            method: 'GET',
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/retailer-groups`,
            params: {
                ...(brandIds?.length ? { brandIds } : {}),
                ...(name ? { name } : {})
            }
        });
    }

    public delete(retailerGroup: RetailerGroup): Promise<void> {
        return this.apiModel.makeHttpRequest({
            method: 'DELETE',
            url: `${this.apiModel.getBrandsEndpoint()}/${retailerGroup.brand._id}/retailer-groups/${retailerGroup._id}`
        });
    }

    public getRetailerGroupExclusiveResources(retailerGroupId: string, brandId: string): Observable<boolean> {
        return this.apiModel.makeHttpObservableRequest<boolean>({
            method: 'GET',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/retailer-groups/${retailerGroupId}/access`
        });
    }

    public getExclusiveMarketingFolders(retailerGroupId: string, brandId: string): Observable<MarketingFolder[]> {
        return this.apiModel.makeHttpObservableRequest<MarketingFolder[]>({
            method: 'GET',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/retailer-groups/${retailerGroupId}/marketing-files`
        });
    }

    public setSelectedGroup(group: RetailerGroup): void {
        this._selectedGroup.next(group);
    }

    public updateRetailerGroup(
        retailerGroup: RetailerGroup,
        pushedRetailerIds?: string[],
        pulledRetailerIds?: string[]
    ): Promise<void> {
        return this.apiModel.makeHttpRequest({
            method: 'PUT',
            url: `${this.apiModel.getBrandsEndpoint()}/${retailerGroup.brand._id}/retailer-groups/${retailerGroup._id}`,
            body: {
                code: retailerGroup.code,
                name: retailerGroup.name,
                description: retailerGroup.description,
                pushedRetailerIds,
                pulledRetailerIds
            }
        });
    }

    public updateRetailerGroupObs(
        retailerGroup: RetailerGroup,
        pushedRetailerIds?: string[],
        pulledRetailerIds?: string[]
    ): Observable<void> {
        return this.apiModel.makeHttpObservableRequest({
            method: 'PUT',
            url: `${this.apiModel.getBrandsEndpoint()}/${retailerGroup.brand._id}/retailer-groups/${retailerGroup._id}`,
            body: {
                code: retailerGroup.code,
                name: retailerGroup.name,
                description: retailerGroup.description,
                pushedRetailerIds,
                pulledRetailerIds
            }
        });
    }

    public fetchSelectedGroup(): void {
        const selectedGroup = this._selectedGroup.getValue();
        if (!selectedGroup) {
            return;
        }
        this.getRetailerGroups(selectedGroup.brand)
            .pipe(take(1))
            .subscribe((groups) => {
                const updatedGroup = _.find(groups, { _id: selectedGroup._id });
                this._selectedGroup.next(updatedGroup);
            });
    }

    public importRetailerGroup(retailerGroupImport: RetailerGroupImport): Promise<ResponseRetailerGroupImportFeedback> {
        const { key, name, type, size } = retailerGroupImport;

        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url: `${this.apiModel.getOrganizationsEndpoint()}/importRetailerGroups`,
            body: {
                key,
                name,
                type,
                size
            }
        });
    }

    public getRetailerGroupImportReportUrl(organizationId: string, fileId: string) {
        return `${restEndpoint}/internal/organizations/${organizationId}/import-summary/${fileId}`;
    }

    public triggerReload() {
        this._updateRetailerGroups.next();
    }
}
